import React, { useState, useMemo } from 'react';
import {
  Box, Typography, IconButton, Button, TextField, Chip, Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import WhatsAppIcon from '@material-ui/icons/WhatsApp';
import SmsIcon from '@material-ui/icons/Sms';
import EmailIcon from '@material-ui/icons/Email';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed', inset: 0, zIndex: 1400,
    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  dialog: {
    width: 560, maxWidth: '94vw', maxHeight: '88vh',
    background: '#fff', borderRadius: 16, overflow: 'hidden',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 60px rgba(0,0,0,0.28)',
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    borderBottom: '1px solid #c7d2fe',
  },
  headerIcon: {
    width: 42, height: 42, borderRadius: 11,
    background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
  },
  title: {
    fontSize: '0.98rem', fontWeight: 700, color: '#1e293b',
  },
  subtitle: {
    fontSize: '0.66rem', color: '#64748b',
  },
  body: {
    padding: '16px 20px', overflow: 'auto', flex: 1,
  },
  contactRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 14, padding: '10px 14px',
    background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0',
  },
  label: {
    fontSize: '0.56rem', fontWeight: 800, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 6,
  },
  templateRow: {
    display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10,
  },
  templateChip: {
    height: 26, fontSize: '0.64rem', fontWeight: 700,
    cursor: 'pointer',
    background: '#fff', color: '#6366f1',
    border: '1.5px solid #c7d2fe',
    '&:hover': { background: '#eef2ff' },
  },
  messageField: {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem', borderRadius: 10,
      background: '#fff',
    },
  },
  actions: {
    display: 'flex', gap: 8,
    padding: '12px 20px', borderTop: '1px solid #e2e8f0',
    background: '#fafbfc',
  },
  sendBtn: {
    flex: 1, textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
    borderRadius: 10, padding: '8px 0', color: '#fff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  noContact: {
    padding: 24, textAlign: 'center',
    background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca',
    color: '#b91c1c',
  },
}));

/**
 * Build templates from the order context.
 * Each template is a factory function that produces the final message string.
 */
function buildTemplates(order, lang = 'pt') {
  const deliveryEta = order.estimatedArrival
    ? new Date(order.estimatedArrival).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })
    : null;
  const deliveryWindow = order.deliveryWindowFrom
    ? `${new Date(order.deliveryWindowFrom).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}`
    : null;
  const deliveryName = (order.deliveryName || order.deliveryAddress || '').split(',')[0];
  const orderNumber = order.orderNumber || `OT-${order.id || ''}`;

  // Compute delay in minutes vs delivery window end
  let delayMin = 0;
  if (order.estimatedArrival && order.deliveryWindowTo) {
    delayMin = Math.round(
      (new Date(order.estimatedArrival).getTime() - new Date(order.deliveryWindowTo).getTime()) / 60000
    );
  }

  if (lang === 'pt') {
    return [
      {
        key: 'delay',
        label: delayMin > 0 ? `Informar atraso (${delayMin}min)` : 'Informar atraso',
        message: delayMin > 0
          ? `Olá, ${order.driverName || ''}. A OT ${orderNumber} vai chegar a ${deliveryName || 'destino'} com ${delayMin}min de atraso (previsto: ${deliveryEta || '—'}). Por favor confirmar recepção desta mensagem.`
          : `Olá, ${order.driverName || ''}. A OT ${orderNumber} vai sofrer um atraso. Por favor informar a posição actual e hora estimada de chegada.`,
      },
      {
        key: 'confirm',
        label: 'Confirmar entrega',
        message: `Olá, ${order.driverName || ''}. Por favor confirmar a entrega da OT ${orderNumber} em ${deliveryName || 'destino'} assim que concluída. Obrigado.`,
      },
      {
        key: 'eta',
        label: 'Pedir ETA',
        message: `Olá, ${order.driverName || ''}. Podes confirmar a hora prevista de chegada a ${deliveryName || 'destino'} (OT ${orderNumber})? Obrigado.`,
      },
      {
        key: 'deposit',
        label: 'Contactar depósito',
        message: `Olá, ${order.driverName || ''}. Por favor contactar o depósito de ${deliveryName || 'destino'} (OT ${orderNumber}) antes da chegada. Aguardando janela ${deliveryWindow || ''}.`,
      },
      {
        key: 'reroute',
        label: 'Alteração de rota',
        message: `Olá, ${order.driverName || ''}. Foi enviada uma versão actualizada da rota da OT ${orderNumber}. Por favor verificar a aplicação e confirmar recepção.`,
      },
    ];
  }
  // English
  return [
    { key: 'delay', label: delayMin > 0 ? `Notify delay (${delayMin}min)` : 'Notify delay',
      message: delayMin > 0
        ? `Hello ${order.driverName || ''}. Order ${orderNumber} will arrive at ${deliveryName || 'destination'} with a ${delayMin}-minute delay (ETA: ${deliveryEta || '—'}). Please confirm receipt.`
        : `Hello ${order.driverName || ''}. Order ${orderNumber} will be delayed. Please share current position and ETA.` },
    { key: 'confirm', label: 'Confirm delivery',
      message: `Hello ${order.driverName || ''}. Please confirm delivery of order ${orderNumber} at ${deliveryName || 'destination'}. Thank you.` },
    { key: 'eta', label: 'Ask for ETA',
      message: `Hello ${order.driverName || ''}. Could you share ETA for order ${orderNumber} at ${deliveryName || 'destination'}? Thanks.` },
    { key: 'deposit', label: 'Contact depot',
      message: `Hello ${order.driverName || ''}. Please contact the ${deliveryName || 'destination'} depot before arrival (order ${orderNumber}).` },
    { key: 'reroute', label: 'Route updated',
      message: `Hello ${order.driverName || ''}. An updated version of the route for order ${orderNumber} has been sent. Please check the app.` },
  ];
}

function sanitizePhone(raw) {
  if (!raw) return '';
  return raw.replace(/[^0-9+]/g, '');
}

function DriverContactDialog({ open, onClose, order, lang = 'pt' }) {
  const classes = useStyles();
  const [message, setMessage] = useState('');
  const [activeTpl, setActiveTpl] = useState(null);
  const [copied, setCopied] = useState(false);

  const templates = useMemo(() => order ? buildTemplates(order, lang) : [], [order, lang]);

  if (!open || !order) return null;

  const driverName = order.driverName || order.driver?.name || '';
  const driverPhone = sanitizePhone(order.driver?.phone || order.driverPhone || '');
  const driverEmail = order.driver?.email || order.driverEmail || '';
  const hasAny = !!(driverPhone || driverEmail);

  const applyTemplate = (tpl) => {
    setMessage(tpl.message);
    setActiveTpl(tpl.key);
  };

  const encodedMsg = encodeURIComponent(message || '');

  const openWhatsApp = () => {
    if (!driverPhone) return;
    // wa.me expects the phone without "+" prefix
    const num = driverPhone.startsWith('+') ? driverPhone.slice(1) : driverPhone;
    window.open(`https://wa.me/${num}?text=${encodedMsg}`, '_blank');
  };
  const openSms = () => {
    if (!driverPhone) return;
    window.location.href = `sms:${driverPhone}?body=${encodedMsg}`;
  };
  const openCall = () => {
    if (!driverPhone) return;
    window.location.href = `tel:${driverPhone}`;
  };
  const openEmail = () => {
    if (!driverEmail) return;
    const subject = encodeURIComponent(`OT ${order.orderNumber || order.id || ''}`);
    window.location.href = `mailto:${driverEmail}?subject=${subject}&body=${encodedMsg}`;
  };
  const copyMessage = async () => {
    if (!message) return;
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <Box className={classes.overlay} onClick={onClose}>
      <Box className={classes.dialog} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerIcon}>
            <PersonIcon style={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box style={{ flex: 1 }}>
            <Typography className={classes.title}>
              {lang === 'pt' ? 'Contactar motorista' : 'Contact driver'}
            </Typography>
            <Typography className={classes.subtitle}>
              {driverName || (lang === 'pt' ? '(sem motorista atribuído)' : '(no driver)')}
              {order.orderNumber && ` · OT ${order.orderNumber}`}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon style={{ fontSize: 20, color: '#64748b' }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box className={classes.body}>
          {/* Contact info */}
          <Box className={classes.contactRow}>
            {driverPhone ? (
              <>
                <PhoneIcon style={{ fontSize: 16, color: '#22c55e' }} />
                <Typography style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a', flex: 1 }}>
                  {driverPhone}
                </Typography>
                <Tooltip title={lang === 'pt' ? 'Ligar' : 'Call'}>
                  <IconButton size="small" onClick={openCall}>
                    <PhoneIcon style={{ fontSize: 14, color: '#22c55e' }} />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <Typography style={{ fontSize: '0.7rem', color: '#94a3b8', fontStyle: 'italic' }}>
                {lang === 'pt' ? 'Sem telefone registado' : 'No phone registered'}
              </Typography>
            )}
          </Box>

          {!hasAny && (
            <Box className={classes.noContact}>
              <Typography style={{ fontSize: '0.82rem', fontWeight: 700 }}>
                {lang === 'pt' ? 'Não há contactos disponíveis' : 'No contact available'}
              </Typography>
              <Typography style={{ fontSize: '0.68rem', marginTop: 4 }}>
                {lang === 'pt'
                  ? 'Adicione telefone ou email no perfil do motorista para enviar mensagens.'
                  : 'Add phone or email to the driver profile to enable messaging.'}
              </Typography>
            </Box>
          )}

          {hasAny && (
            <>
              {/* Quick templates */}
              <Typography className={classes.label}>
                {lang === 'pt' ? 'Modelos rápidos' : 'Quick templates'}
              </Typography>
              <Box className={classes.templateRow}>
                {templates.map(tpl => (
                  <Chip key={tpl.key}
                    label={tpl.label}
                    className={classes.templateChip}
                    onClick={() => applyTemplate(tpl)}
                    style={activeTpl === tpl.key ? { background: '#6366f1', color: '#fff', borderColor: '#4f46e5' } : undefined}
                  />
                ))}
              </Box>

              {/* Message editor */}
              <Typography className={classes.label} style={{ marginTop: 10 }}>
                {lang === 'pt' ? 'Mensagem' : 'Message'}
              </Typography>
              <TextField
                className={classes.messageField}
                variant="outlined"
                multiline
                minRows={4}
                maxRows={8}
                fullWidth
                value={message}
                onChange={(e) => { setMessage(e.target.value); setActiveTpl(null); }}
                placeholder={lang === 'pt' ? 'Escrever uma mensagem ou escolher um modelo acima…' : 'Type a message or pick a template above…'}
              />
            </>
          )}
        </Box>

        {/* Actions */}
        {hasAny && (
          <Box className={classes.actions}>
            {driverPhone && (
              <Tooltip title={lang === 'pt' ? 'Abrir WhatsApp Web/App' : 'Open WhatsApp'}>
                <Button className={classes.sendBtn}
                  style={{ background: '#25D366' }}
                  onClick={openWhatsApp}
                  disabled={!message}>
                  <WhatsAppIcon style={{ fontSize: 16, marginRight: 6 }} />
                  WhatsApp
                </Button>
              </Tooltip>
            )}
            {driverPhone && (
              <Tooltip title={lang === 'pt' ? 'Enviar por SMS' : 'Send SMS'}>
                <Button className={classes.sendBtn}
                  style={{ background: '#3b82f6' }}
                  onClick={openSms}
                  disabled={!message}>
                  <SmsIcon style={{ fontSize: 16, marginRight: 6 }} />
                  SMS
                </Button>
              </Tooltip>
            )}
            {driverEmail && (
              <Tooltip title={lang === 'pt' ? 'Enviar por email' : 'Send email'}>
                <Button className={classes.sendBtn}
                  style={{ background: '#8b5cf6' }}
                  onClick={openEmail}
                  disabled={!message}>
                  <EmailIcon style={{ fontSize: 16, marginRight: 6 }} />
                  Email
                </Button>
              </Tooltip>
            )}
            <Tooltip title={copied ? (lang === 'pt' ? 'Copiado!' : 'Copied!') : (lang === 'pt' ? 'Copiar texto' : 'Copy text')}>
              <Button className={classes.sendBtn}
                style={{ background: copied ? '#22c55e' : '#64748b' }}
                onClick={copyMessage}
                disabled={!message}>
                {copied ? <CheckCircleIcon style={{ fontSize: 16, marginRight: 6 }} /> : <FileCopyIcon style={{ fontSize: 16, marginRight: 6 }} />}
                {copied ? (lang === 'pt' ? 'OK' : 'OK') : (lang === 'pt' ? 'Copiar' : 'Copy')}
              </Button>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default DriverContactDialog;
